import { useState } from 'react';
import styled from 'styled-components';
import { FlexColumn } from '@elementary/common-ui/components';
import useDataHealthFilters from 'dataHealth/hooks/useDataHealthFilters';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import { colorTokens } from '@elementary/common-ui/style/tokens';
import { space } from '@elementary/common-ui/style/utils';
import { ScoreOverTimeChartPoints, ThresholdLevel } from '../../types';
import { getTestResultsLink, getValueColor } from '../../utils';
import EmptyState from '../shared/EmptyState';
import ScoreOverTimeChart from '../shared/ScoreOverTimeChart';
import TextWithLabel from '../shared/TextWithLabel';
import Widget from '../shared/Widget';
import ScoreThresholdsDialog from './ScoreThresholdsDialog';

const Grid = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: ${space('md', 'xxl')};
  row-gap: ${space('md')};
  column-gap: ${space('xxxl')};
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.divider};

  [data-narrow] & {
    column-gap: ${space('lg')};
  }
`;

type Props = {
  title: string;
  tooltip: string;
  score?: number;
  tests?: number;
  monitoredTables?: number;
  thresholdLevel?: ThresholdLevel;
  scores?: ScoreOverTimeChartPoints[];
  low?: number;
  medium?: number;
  testResultsTableColumnKey: string;
  onQualityDimensionThresholdsChange: () => void;
};

const getEmptyStateColor = (value: number) =>
  value === 0 ? colorTokens.chart.axis : colorTokens.content.primary;

const QualityDimensionScore = ({
  title,
  tooltip,
  score = 0,
  tests = 0,
  monitoredTables = 0,
  thresholdLevel = null,
  scores = [],
  low = 0,
  medium = 0,
  testResultsTableColumnKey,
  onQualityDimensionThresholdsChange,
}: Props) => {
  const [isSettingsDialogOpened, setIsSettingsDialogOpen] = useState(false);
  const { urlFilters } = useDataHealthFilters();
  const [searchParams] = useSearchParams();

  return (
    <Widget
      title={title}
      tooltip={tooltip}
      thresholdLevel={thresholdLevel}
      onSettingsClicked={() => setIsSettingsDialogOpen(true)}
    >
      <Grid>
        <TextWithLabel
          title="Score"
          value={score === 0 ? '0' : `${Math.round(score)}%`}
          valueColor={getValueColor(thresholdLevel)}
        />

        <TextWithLabel
          title="Tests"
          value={String(tests)}
          valueColor={getEmptyStateColor(tests)}
          titleLink={getTestResultsLink(
            searchParams,
            urlFilters,
            testResultsTableColumnKey,
          )}
        />

        <TextWithLabel
          title="Monitored Tables"
          value={String(monitoredTables)}
          valueColor={getEmptyStateColor(tests)}
        />
      </Grid>

      <FlexColumn>
        {tests === 0 ? (
          <EmptyState />
        ) : (
          <ScoreOverTimeChart
            height={156}
            scores={scores}
            lowThreshold={low}
            mediumThreshold={medium}
            hideXAxis={false}
            small
          />
        )}
      </FlexColumn>

      {isSettingsDialogOpened && (
        <ScoreThresholdsDialog
          title={title}
          description={tooltip}
          open={isSettingsDialogOpened}
          onClose={() => {
            setIsSettingsDialogOpen(false);
          }}
          onSave={() => {
            onQualityDimensionThresholdsChange();
            setIsSettingsDialogOpen(false);
          }}
        />
      )}
    </Widget>
  );
};

export default QualityDimensionScore;
